import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import type { ApiResponse } from "@ridereserv/shared/types";
import { supabase } from "./supabase";
import { useAuthStore } from "@/stores/authStore";
import { env } from "./env";

// Re-export ApiResponse for convenience
export type { ApiResponse };

// Create axios instance with defaults
export const api: AxiosInstance = axios.create({
  baseURL: env.VITE_API_URL ?? "",
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - get token from Supabase (single source of truth)
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // Skip if Authorization header is already set (e.g., explicit override)
    if (config.headers.Authorization) {
      return config;
    }

    // Get token from Supabase - it handles storage and refresh automatically
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.access_token) {
        config.headers.Authorization = `Bearer ${session.access_token}`;
      }
    } catch {
      // Continue without token - request may fail with 401
    }

    return config;
  },
  (error: Error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<{ error: { code: string; message: string } }>) => {
    // Handle 401 - session may have expired
    if (error.response?.status === 401) {
      // Try to refresh the session first
      const { data: { session } } = await supabase.auth.getSession();

      if (!session) {
        // No valid session - clear UI state and redirect to login
        useAuthStore.getState().clearAuth();
        if (!window.location.pathname.startsWith("/login")) {
          window.location.href = "/login";
        }
      }
      // If session exists but we still got 401, it might be a server-side issue
      // Don't automatically logout - let the specific page handle the error
    }
    return Promise.reject(error);
  }
);
